import { useCallback, useEffect, useMemo, useState } from 'react'
import { Alert, Box, CircularProgress, Snackbar } from '@mui/material'
import {
  Deposit,
  Fund,
  PositionImportResult,
  StrategyKey,
  TotalUnitsChange,
  UnitBatch,
  Valuation,
} from '../api/types'
import { useOpsApi } from '../api/context'
import { apiError } from '../api/errors'
import { useCurrentUser } from '../auth'
import { InfoHint } from '../components/InfoHint'
import { PageHeader } from '../components/PageHeader'
import { SegmentedTabs } from '../components/SegmentedTabs'
import { BatchDetailDialog } from './jednotky/BatchDetailDialog'
import { BatchHistoryCard } from './jednotky/BatchHistoryCard'
import { BatchPreviewCard } from './jednotky/BatchPreviewCard'
import { FundOverviewCard } from './jednotky/FundOverviewCard'
import { InstrumentPriceCard } from './jednotky/InstrumentPriceCard'
import { NavValuationCard } from './jednotky/NavValuationCard'
import { OverrideTarget, OverrideValue, OverrideDialog } from './jednotky/OverrideDialog'
import { PaidQueueCard } from './jednotky/PaidQueueCard'
import { RedemptionQueueCard } from './jednotky/RedemptionQueueCard'
import { TotalUnitsDialog } from './jednotky/TotalUnitsDialog'
import { formatUnits } from './jednotky/format'

type Overrides = Record<string, OverrideValue>

/**
 * Fáze 4 — Přidělení jednotek (jen CEO, gating řeší phases.ts): pro vybraný fond
 * NAV ocenění → cena jednotky → výběr účastníků z fronty → náhled → DRAFT přepočet,
 * který pokračuje do fáze Kontrola. Veškerá matematika jednotek jde přes calc/preview.
 */
export function JednotkyPhase() {
  const api = useOpsApi()
  const user = useCurrentUser()

  const [funds, setFunds] = useState<Fund[]>([])
  const [fundKey, setFundKey] = useState<StrategyKey>('SEMI')
  const [valuations, setValuations] = useState<Valuation[]>([])
  const [batches, setBatches] = useState<UnitBatch[]>([])
  // Jen PAID vklady vybraného fondu čekají na přidělení (IN_BATCH/POSTED jsou pryč).
  const [paidDeposits, setPaidDeposits] = useState<Deposit[]>([])

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [snackbar, setSnackbar] = useState<string | null>(null)

  // Výběr účastníků a ruční výjimky drží fáze (lift state) — reset při přepnutí fondu.
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [overrides, setOverrides] = useState<Overrides>({})
  const [overrideTargetId, setOverrideTargetId] = useState<string | null>(null)
  const [detailBatch, setDetailBatch] = useState<UnitBatch | null>(null)

  // Ruční oprava celkových jednotek (jen CEO) + její audit.
  const [totalUnitsOpen, setTotalUnitsOpen] = useState(false)
  const [unitsHistory, setUnitsHistory] = useState<TotalUnitsChange[]>([])
  const [unitsHistoryError, setUnitsHistoryError] = useState<string | null>(null)
  // Po opravě zůstává na stránce, dokud ji obsluha nezavře — je to pokyn k další akci.
  const [unitsNotice, setUnitsNotice] = useState<string | null>(null)

  const fund = useMemo(() => funds.find((f) => f.key === fundKey), [funds, fundKey])
  const latestValuation = valuations[0]

  // Fond-scoped vklady = PAID dané strategie (mock vrací i jiné fondy).
  const filterFundDeposits = useCallback(
    (list: Deposit[], key: StrategyKey) => list.filter((d) => d.strategy === key),
    []
  )

  // Kompletní (re)load po přepnutí fondu i při prvním otevření.
  const reloadAll = useCallback(
    async (key: StrategyKey) => {
      setLoading(true)
      setError(null)
      try {
        const [f, v, b, d] = await Promise.all([
          api.listFunds(),
          api.listValuations(key),
          api.listBatches(key),
          api.listDeposits({ status: 'PAID' }),
        ])
        setFunds(f)
        setValuations(v)
        setBatches(b)
        setPaidDeposits(filterFundDeposits(d, key))
      } catch (err) {
        setError(apiError(err, 'Načtení dat fondu selhalo'))
      } finally {
        setLoading(false)
      }
    },
    [api, filterFundDeposits]
  )

  /**
   * Audit ručních oprav jednotek se načítá zvlášť: chyba (nebo BE, který endpoint
   * ještě nemá) nesmí shodit celou fázi — hlásí se jen v místě tabulky.
   */
  const reloadUnitsHistory = useCallback(
    async (key: StrategyKey) => {
      setUnitsHistoryError(null)
      try {
        setUnitsHistory(await api.listTotalUnitsHistory(key))
      } catch (err) {
        setUnitsHistory([])
        setUnitsHistoryError(apiError(err, 'Historii ručních oprav jednotek se nepodařilo načíst'))
      }
    },
    [api]
  )

  useEffect(() => {
    reloadAll(fundKey)
  }, [reloadAll, fundKey])

  useEffect(() => {
    reloadUnitsHistory(fundKey)
  }, [reloadUnitsHistory, fundKey])

  const handleFundChange = (key: string) => {
    setFundKey(key as StrategyKey)
    // Výběr i overridy patří jinému fondu — vyčistit (effect výše přenačte data).
    setSelected(new Set())
    setOverrides({})
    setUnitsNotice(null)
  }

  const handleInstrumentPriceUpdated = useCallback(async () => {
    setSnackbar('Cena instrumentu aktualizována. Hodnoty portfolií se projeví po nočním přepočtu.')
    try {
      setValuations(await api.listValuations(fundKey))
    } catch (err) {
      setError(apiError(err, 'Načtení ocenění selhalo'))
    }
  }, [api, fundKey])

  // Po uložení ocenění: reload fondů (nová lastValuation) + ocenění téhož fondu.
  const handleValuationCreated = useCallback(async () => {
    setSnackbar('Ocenění uloženo')
    try {
      const [f, v] = await Promise.all([api.listFunds(), api.listValuations(fundKey)])
      setFunds(f)
      setValuations(v)
    } catch (err) {
      setError(apiError(err, 'Načtení ocenění selhalo'))
    }
  }, [api, fundKey])

  // Po vytvoření přepočtu: vyčistit výběr/overridy + reload vkladů (PAID → IN_BATCH) a historie.
  const handleBatchCreated = useCallback(async () => {
    setSnackbar('Přepočet vytvořen (DRAFT) — pokračuje fáze Kontrola')
    setSelected(new Set())
    setOverrides({})
    try {
      const [b, d] = await Promise.all([
        api.listBatches(fundKey),
        api.listDeposits({ status: 'PAID' }),
      ])
      setBatches(b)
      setPaidDeposits(filterFundDeposits(d, fundKey))
    } catch (err) {
      setError(apiError(err, 'Načtení fronty selhalo'))
    }
  }, [api, fundKey, filterFundDeposits])

  /**
   * Po ruční opravě jednotek: zavřít dialog, přenačíst fond (nový total) i ocenění a
   * říct obsluze, co teď platí — uložená ocenění si drží SVOU cenu, takže dokud
   * nevznikne nové ocenění, počítalo by se přidělení i fee ze staré ceny.
   */
  const handleTotalUnitsSaved = useCallback(
    async (saved: Fund) => {
      setTotalUnitsOpen(false)
      setSnackbar('Celkový počet jednotek upraven')
      setUnitsNotice(
        `Fond ${saved.name} má nově ${formatUnits(saved.totalUnits)} jednotek. Uložená ocenění si drží svou původní cenu jednotky — než pustíš další přidělení jednotek nebo běh fee, zadej nové ocenění (NAV), aby se cena přepočítala z nového počtu jednotek.`
      )
      try {
        const [f, v] = await Promise.all([api.listFunds(), api.listValuations(fundKey)])
        setFunds(f)
        setValuations(v)
      } catch (err) {
        setError(apiError(err, 'Načtení dat fondu selhalo'))
      }
      reloadUnitsHistory(fundKey)
    },
    [api, fundKey, reloadUnitsHistory]
  )

  const toggleSelected = (depositId: string) =>
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(depositId)) next.delete(depositId)
      else next.add(depositId)
      return next
    })
  const toggleAll = (checked: boolean) =>
    setSelected(checked ? new Set(paidDeposits.map((d) => d.id)) : new Set())

  const handleSaveOverride = (override: OverrideValue | null) => {
    const id = overrideTargetId
    if (id) {
      setOverrides((prev) => {
        const next = { ...prev }
        if (!override) delete next[id]
        else next[id] = override
        return next
      })
    }
    setOverrideTargetId(null)
  }

  // Náhled drží pořadí fronty (paidAt asc) — listDeposits vrací createdAt desc.
  const selectedDeposits = useMemo(
    () =>
      paidDeposits
        .filter((d) => selected.has(d.id))
        .sort((a, b) => ((a.paidAt ?? '') < (b.paidAt ?? '') ? -1 : 1)),
    [paidDeposits, selected]
  )

  // Cíl override dialogu = vklad + cena aktuálního ocenění (nutná pro živý náhled jednotek).
  const overrideTarget: OverrideTarget | null = useMemo(() => {
    if (!overrideTargetId || !latestValuation) return null
    const deposit = paidDeposits.find((d) => d.id === overrideTargetId)
    if (!deposit) return null
    return {
      depositId: deposit.id,
      clientName: deposit.clientName,
      amount: deposit.amountReceived ?? deposit.amount,
      unitPrice: latestValuation.unitPrice,
    }
  }, [overrideTargetId, latestValuation, paidDeposits])

  // Loader kryje i přepnutí fondu (reloadAll) — zabrání bliknutí dat starého fondu;
  // reload po ocenění/přepočtu loading nezapíná, takže formuláře neproblikávají.
  const showLoader = loading

  return (
    <Box sx={{ maxWidth: 1100, display: 'flex', flexDirection: 'column', gap: 3 }}>
      <PageHeader
        title="Přidělení jednotek"
        subtitle="NAV → cena jednotky → výběr účastníků → náhled → DRAFT přepočet do fáze Kontrola."
        info={
          <InfoHint
            title="Jak se počítají jednotky"
            lines={[
              'Cena jednotky = NAV fondu / celkový počet jednotek (z vybraného ocenění; jeden fond = jedna cena pro všechny třídy).',
              'Jednotky účastníka = přijatá částka / cena jednotky (zaokrouhleno na 8 desetinných míst).',
              'Per účastník lze ručně přepsat cenu nebo počet jednotek (override) — kontrola je pak hlídá.',
              'Datum přidělení musí být ≥ datum potvrzení všech plateb v přepočtu.',
              'Potvrzením přepočtu (fáze Kontrola) se jednotky připíší klientům a total_units fondu se navýší.',
            ]}
          />
        }
      />

      <SegmentedTabs
        items={funds.map((f) => ({ value: f.key, label: f.key }))}
        value={fundKey}
        onChange={handleFundChange}
      />

      {error && <Alert severity="error">{error}</Alert>}

      {unitsNotice && (
        <Alert severity="warning" onClose={() => setUnitsNotice(null)}>
          {unitsNotice}
        </Alert>
      )}

      {showLoader ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : fund ? (
        <>
          <FundOverviewCard
            fund={fund}
            queueDeposits={paidDeposits}
            canEditTotalUnits={user.isCeo}
            onEditTotalUnits={() => setTotalUnitsOpen(true)}
            unitsHistory={unitsHistory}
            unitsHistoryError={unitsHistoryError}
          />

          <NavValuationCard
            fund={fund}
            latestValuation={latestValuation}
            onCreated={handleValuationCreated}
          />

          <InstrumentPriceCard
            latestValuation={latestValuation}
            canUpdate={user.isCeo}
            onUpdated={handleInstrumentPriceUpdated}
          />

          <RedemptionQueueCard fundKey={fundKey} latestValuation={latestValuation ?? null} />

          <PaidQueueCard
            fundKey={fundKey}
            deposits={paidDeposits}
            selected={selected}
            onToggle={toggleSelected}
            onToggleAll={toggleAll}
          />

          <BatchPreviewCard
            fund={fund}
            latestValuation={latestValuation}
            selectedDeposits={selectedDeposits}
            overrides={overrides}
            onEditOverride={setOverrideTargetId}
            onCreated={handleBatchCreated}
          />

          <BatchHistoryCard fundKey={fundKey} batches={batches} onRowClick={setDetailBatch} />
        </>
      ) : null}

      {fund && user.isCeo && (
        <TotalUnitsDialog
          open={totalUnitsOpen}
          fund={fund}
          onSaved={handleTotalUnitsSaved}
          onClose={() => setTotalUnitsOpen(false)}
        />
      )}

      <OverrideDialog
        open={Boolean(overrideTargetId)}
        target={overrideTarget}
        existing={overrideTargetId ? overrides[overrideTargetId] : undefined}
        onSave={handleSaveOverride}
        onClose={() => setOverrideTargetId(null)}
      />

      <BatchDetailDialog
        open={Boolean(detailBatch)}
        batch={detailBatch}
        onClose={() => setDetailBatch(null)}
      />

      <Snackbar
        open={Boolean(snackbar)}
        autoHideDuration={5000}
        onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        message={snackbar ?? ''}
      />
    </Box>
  )
}
